import React, { useEffect, useRef, useState } from "react";
import Icon from "./icons";

// Invite partners to the open session (artemisapi docs/client-guide-group-chats.md): pick BaseMap
// friends to add now, or copy a 7-day link for anyone else. Adding someone turns the chat into a
// shared one. api: { friends(), add(userIds), link() } as promises; members: who's in already.

const initialsOf = name => String(name || '?').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'

const InvitePanel = ({ api, members, onClose, onDone }) => {
  const ref = useRef(null)
  const [friends, setFriends] = useState(null)
  const [picked, setPicked] = useState(() => new Set())
  const [busy, setBusy] = useState(null)
  const [note, setNote] = useState(null)

  useEffect(() => {
    api.friends().then(list => setFriends(Array.isArray(list) ? list : [])).catch(() => setFriends([]))
    const onKey = e => { if (e.key === 'Escape') onClose() }
    const onDown = e => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.artemis-invite-button')) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [])

  const inChat = new Set(members.map(m => String(m.UserId)))
  const choices = (friends || []).filter(f => f && f.id != null && !inChat.has(String(f.id)))
  const toggle = id => setPicked(set => { const next = new Set(set); next.has(id) ? next.delete(id) : next.add(id); return next })
  const fail = err => setNote((err && err.userMessage) || 'Something went wrong. Try again.')

  const add = () => {
    if (picked.size === 0) return
    setBusy('add'); setNote(null)
    api.add([...picked])
      .then(() => onDone(`Added ${picked.size === 1 ? '1 partner' : `${picked.size} partners`}`))
      .catch(fail)
      .finally(() => setBusy(null))
  }

  // The system share sheet where the browser has one (as Android's share), alongside Copy.
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  const shareLink = () => {
    setBusy('share'); setNote(null)
    api.link()
      .then(url => navigator.share({ title: 'Join my BaseMap session', text: 'Plan this hunt with me in BaseMap', url })
        .then(() => onDone('Invite link shared. It works for 7 days.'), e => { if (!e || e.name !== 'AbortError') setNote(url) }))
      .catch(fail)
      .finally(() => setBusy(null))
  }

  const copyLink = () => {
    setBusy('link'); setNote(null)
    api.link()
      .then(url => (navigator.clipboard && navigator.clipboard.writeText ? navigator.clipboard.writeText(url) : Promise.reject()).then(
        () => onDone('Invite link copied. It works for 7 days.'),
        () => setNote(url)))
      .catch(fail)
      .finally(() => setBusy(null))
  }

  return (
    <div className="artemis-popover artemis-invite" role="dialog" aria-label="Invite partners" ref={ref}>
      <span className="artemis-eyebrow">Invite partners</span>
      <p className="artemis-invite-text">Partners see the chat and the scratchpad, and can @mention you or Artemis.</p>
      <div className="artemis-invite-list">
        {friends === null && <p className="artemis-muted">Loading your friends…</p>}
        {friends !== null && choices.length === 0 && <p className="artemis-muted">{friends.length ? 'Your friends are all in this chat.' : 'Add friends in BaseMap, or send a link.'}</p>}
        {choices.map(f => (
          <button key={f.id} type="button" role="checkbox" aria-checked={picked.has(f.id)} className="artemis-invite-friend" onClick={() => toggle(f.id)}>
            <span className={`artemis-scratch-check ${picked.has(f.id) ? 'artemis-scratch-check-on' : ''}`} aria-hidden="true">
              {picked.has(f.id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>}
            </span>
            <span className="artemis-avatar">{initialsOf(f.fullName)}</span>
            <span className="artemis-flex">{f.fullName || 'Friend'}</span>
          </button>
        ))}
      </div>
      {note && <p className="artemis-invite-note" role="status">{note}</p>}
      <div className="artemis-invite-actions">
        <button type="button" className="artemis-small-button" onClick={copyLink} disabled={busy != null}>
          <Icon name="copy" size={14} /> {busy === 'link' ? 'Making a link…' : 'Copy invite link'}
        </button>
        {canShare && (
          <button type="button" className="artemis-small-button" onClick={shareLink} disabled={busy != null}>
            <Icon name="share" size={14} /> {busy === 'share' ? 'Making a link…' : 'Share'}
          </button>
        )}
        <span className="artemis-flex" />
        <button type="button" className="artemis-invite-add" onClick={add} disabled={picked.size === 0 || busy != null}>
          {busy === 'add' ? 'Adding…' : picked.size ? `Add ${picked.size}` : 'Add'}
        </button>
      </div>
    </div>
  )
}

export default InvitePanel
